import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import { QRCodeSVG } from "qrcode.react";
import { post } from "aws-amplify/api";
import StepUpDialog from "../components/StepUpDialog";

async function readErrorBody(error) {
  const candidates = [
    error?.response?.body,
    error?.response,
    error?.underlyingError?.response?.body,
    error?.cause?.response?.body,
    error?.body,
  ];
  for (const src of candidates) {
    if (src == null) continue;
    if (typeof src === "object" && (src.error || src.detail || src.reference)) return src;
    if (typeof src?.json === "function") {
      try {
        return await Promise.race([
          src.json(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
      } catch (_) { /* */ }
    }
    if (typeof src?.text === "function") {
      try {
        const text = await Promise.race([
          src.text(),
          new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 1000))
        ]);
        if (typeof text === "string" && text) return JSON.parse(text);
      } catch (_) { /* */ }
    }
    if (typeof src === "string" && src) {
      try { return JSON.parse(src); } catch (_) { /* */ }
    }
  }
  return null;
}

export default function SetupMfa() {
  const navigate = useNavigate();
  // Stages: requesting → (needs-step-up | enrol) → verifying → done; plus error.
  const [stage, setStage] = useState("requesting");
  const [setupUri, setSetupUri] = useState("");
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [stepUpOpen, setStepUpOpen] = useState(false);

  const startedRef = useRef(false);

  // Calls /step-up/setup. Server enforces:
  //  - already-enrolled callers must have an unexpired step-up session,
  //    otherwise it returns 401 STEP_UP_REQUIRED. The FE catches that
  //    and shows the "Replace your MFA" gate.
  const requestSetup = async () => {
    setStage("requesting");
    setErrorMsg("");
    try {
      const op = post({
        apiName: "license",
        path: "/step-up/setup",
        options: { body: {} }
      });
      const { body } = await op.response;
      const data = await body.json();
      setSetupUri(data?.setupUri ?? "");
      setSecret(data?.secret ?? "");
      setStage("enrol");
    } catch (e) {
      const errorBody = await readErrorBody(e);
      if (errorBody?.error === "STEP_UP_REQUIRED") {
        setStage("needs-step-up");
        return;
      }
      setErrorMsg(errorBody?.detail ?? errorBody?.error ?? e?.message ?? "Failed to start MFA setup");
      setStage("error");
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    requestSetup();
  }, []);

  const onConfirm = async () => {
    if (!/^\d{6}$/.test(code)) {
      setErrorMsg("Code must be 6 digits.");
      return;
    }
    setStage("verifying");
    setErrorMsg("");
    try {
      const op = post({
        apiName: "license",
        path: "/step-up/verify",
        options: { body: { code } }
      });
      await op.response;
      setStage("done");
    } catch (e) {
      setStage("enrol");
      const errorBody = await readErrorBody(e);
      if (errorBody?.error === "INVALID_CODE") {
        setErrorMsg("That code didn't match. Try the next one your authenticator shows.");
      } else if (errorBody?.error === "NOT_ENROLLED") {
        setErrorMsg("Setup didn't complete — refresh the page and try again.");
      } else {
        setErrorMsg(errorBody?.detail ?? errorBody?.error ?? e?.message ?? "Verification failed.");
      }
      setCode("");
    }
  };

  // After step-up succeeds, the elevated session is written, so the
  // next /step-up/setup call will return a fresh secret.
  const onStepUpVerified = async () => {
    setStepUpOpen(false);
    await requestSetup();
  };

  if (stage === "requesting" || stage === "verifying") {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (stage === "needs-step-up") {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
          Replace your MFA
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You're already set up. To register a new authenticator (e.g. you've got a new phone), confirm your current MFA first — then you'll get a fresh QR to scan.
        </Typography>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Continuing will <strong>replace</strong> your current MFA. Your existing authenticator entry will stop working.
            </Alert>
            <Stack direction="row" spacing={1.5}>
              <Button variant="contained" onClick={() => setStepUpOpen(true)}>
                Verify with current MFA
              </Button>
              <Button onClick={() => navigate(-1)}>Cancel</Button>
            </Stack>
          </CardContent>
        </Card>

        <StepUpDialog
          open={stepUpOpen}
          onClose={() => setStepUpOpen(false)}
          onVerified={onStepUpVerified}
        />
      </Box>
    );
  }

  if (stage === "error") {
    return (
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 3 }}>
          Set up MFA
        </Typography>
        <Alert severity="error">{errorMsg}</Alert>
      </Box>
    );
  }

  if (stage === "done") {
    return (
      <Box>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
          <VerifiedIcon color="success" />
          <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em" }}>
            MFA set up
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          You'll be asked for a code from your authenticator app whenever you edit a licence
          or create a new organisation. Codes are good for 15 minutes once verified.
        </Typography>
        <Stack direction="row" spacing={1.5}>
          <Button variant="contained" onClick={() => navigate("/admin/licenses")}>
            Back to licences
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, letterSpacing: "-0.02em", mb: 1 }}>
        Set up MFA
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Scan the QR code with an authenticator app (Google Authenticator, 1Password,
        Authy, Microsoft Authenticator…), then enter the 6-digit code it generates.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={4} alignItems={{ md: "center" }}>
            <Box
              sx={{
                p: 2,
                bgcolor: "common.white",
                borderRadius: 2,
                display: "inline-flex",
                alignSelf: { xs: "center", md: "flex-start" }
              }}
            >
              {setupUri ? <QRCodeSVG value={setupUri} size={192} /> : null}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="overline" color="text.secondary">
                Can't scan? Enter manually
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontFamily: '"JetBrains Mono", "SF Mono", Menlo, Consolas, monospace',
                  fontSize: "0.95rem",
                  fontWeight: 500,
                  wordBreak: "break-all",
                  mt: 1,
                  py: 1,
                  px: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1
                }}
              >
                {secret}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                Type "DataStar" as the account name in your authenticator if it asks.
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 3 }} />

          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Enter the current 6-digit code from your authenticator to confirm.
            </Typography>
            {errorMsg ? <Alert severity="error">{errorMsg}</Alert> : null}
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
              <TextField
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                label="Authentication code"
                placeholder="123456"
                autoFocus
                inputProps={{
                  inputMode: "numeric",
                  autoComplete: "one-time-code",
                  pattern: "[0-9]*",
                  maxLength: 6
                }}
                sx={{ maxWidth: 220 }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && code.length === 6) onConfirm();
                }}
              />
              <Button
                variant="contained"
                onClick={onConfirm}
                disabled={code.length !== 6}
              >
                Verify
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
